import { TranslationFunction } from '@/src/types/translations'
import { Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material'
import Image from 'next/image'

interface InsuranceFooterProps {
    insuranceTermsAndConditionsError: boolean
    termsAndConditionsUrl: string
    isInsuranceTermsAndConditionsChecked: boolean
    onTermsChange: () => void
    t: TranslationFunction<'common'>
}

const InsuranceFooter: React.FC<InsuranceFooterProps> = ({
    insuranceTermsAndConditionsError,
    termsAndConditionsUrl,
    isInsuranceTermsAndConditionsChecked,
    onTermsChange,
    t
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { sm: 'flex-start', md: 'center' },
                p: { sm: 0, md: 2 },
                bgcolor: 'grey.50',
                borderRadius: 2,
                // mt: 1,
                gap: 0,
                flexDirection: { sm: 'column-reverse', md: 'row' }
            }}
        >
            {/* Left side - TripAdd branding and view terms */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { md: 'left', lg: 'center' },
                    gap: 1,
                    flexDirection: { md: 'column', lg: 'row' },
                    marginLeft: { sm: 1, md: 0 }
                }}
            >
                {/* TripAdd Logo */}
                <Image
                    src="https://app.tripadd.com/assets/tripadd/logo.png"
                    alt="TripAdd Logo"
                    width={64}
                    height={16}
                    style={{ marginTop: 4 }}
                />

                {/* View Terms Link */}
                {termsAndConditionsUrl && (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                            window.open(termsAndConditionsUrl, '_blank')
                        }}
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 500,
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            textAlign: { md: 'left', lg: 'center' },
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {t('viewTermsAndConditions')}
                    </Link>
                )}
            </Box>

            {/* Right side - Acceptance checkbox */}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={isInsuranceTermsAndConditionsChecked}
                        onChange={onTermsChange}
                        size="small"
                        disableFocusRipple
                        disableTouchRipple
                        disableRipple
                        sx={{
                            color: insuranceTermsAndConditionsError ? 'error.main' : 'primary.main',
                            '&.Mui-checked': {
                                color: insuranceTermsAndConditionsError ? 'error.main' : 'primary.main'
                            },
                            ...(insuranceTermsAndConditionsError && {
                                '&:not(.Mui-checked)': {
                                    color: 'error.main'
                                }
                            })
                        }}
                    />
                }
                label={
                    <Typography
                        variant="body2"
                        sx={{
                            color: insuranceTermsAndConditionsError ? 'error.main' : 'text.secondary',
                            fontSize: {xs: '0.75rem', md: '0.875rem'},
                            ...(insuranceTermsAndConditionsError && {
                                fontWeight: 500
                            })
                        }}
                    >
                        {t('acceptTheTermsAndConditionsOfThisPolicy')}
                    </Typography>
                }
                sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                        ml: 0.5
                    }
                }}
            />
        </Box>
    )
}

export default InsuranceFooter
export type { InsuranceFooterProps }
